// strafe.games — SIGNAL DECK global platform panel (plain DOM overlay, zero deps).
//
// CANONICAL COPY. Mirrored verbatim into every game's src/ (games share no
// build). Only edit the canonical copy here, then re-sync. An inline twin lives
// on the hub.
//
// This is the full "account hub" the credit handle opens: an edge-docked panel
// ("rack") whose five areas (ACCOUNT, CREDITS [balance + store + history],
// STATS, GAMES, SETTINGS) are navigated by a persistent vertical rail + sliding
// orange needle. Palette mirrors the hub: #FF4500 orange on off-white ink over
// cool near-black surfaces. Framework-agnostic — it injects ONE ref-counted
// <style> block whose every selector is `.strafe-panel-`-prefixed (no tag or
// global selectors), so it cannot collide with a game's own CSS.
//
// LAYOUT/ANIM: a dimmed backdrop (fade) + the rack. Desktop / iPad (>=640px) is
// a right-edge full-height dock that SLIDES IN FROM THE RIGHT; a left-edge
// handle (the closed chip handle, having "moved left" with the drawer) closes
// it. iPhone (<640px) is a bottom sheet that slides up (safe-area padded) with a
// top grab-bar to close + the rail rotated to a horizontal scroll-snap strip.
// Close on handle / grab-bar / backdrop / Escape, with a focus trap,
// roving-tabindex rail nav, and focus restored to the opener. role="dialog".
// Honours `prefers-reduced-motion`.
//
// DORMANT-SAFE: with auth disabled the ACCOUNT channel shows a subtle "coming
// soon" note and the panel makes ZERO server calls; credits(local) / stats /
// nav still work. NEVER THROWS — every platform/auth read is guarded.
//
// DEBUG: on boot, if location.search includes `panel` (e.g. ?panel or ?panel=1)
// the panel auto-opens once, so the look can be screenshot-verified without a
// click. Tiny + harmless in prod.
//
// Returns a no-op handle when `document` is absent (SSR / headless).

import type { Platform as PlatformType } from './platform';

type Platform = typeof PlatformType;

export interface AuthSessionLike {
  userId: string;
  displayName: string;
  email?: string;
  avatarUrl: string | null;
}

export type AuthResultLike = { ok: true } | { ok: false; error: string };

/** The minimal slice of an auth client the panel needs. Kept structural so any
 *  auth module (the canonical `auth.ts`, or a game-specific one) satisfies it.
 *  `login` / `register` are OPTIONAL — a client lacking them still mounts; the
 *  panel guards every call (so the form degrades to an inert error). */
export interface AuthClientLike {
  isAuthEnabled(): boolean;
  getSession(): Promise<AuthSessionLike | null>;
  signOut(): Promise<void> | void;
  subscribe(fn: (s: AuthSessionLike | null) => void): () => void;
  login?(email: string, password: string): Promise<AuthResultLike>;
  register?(email: string, password: string, turnstileToken?: string): Promise<AuthResultLike>;
}

export type SectionId = 'account' | 'credits' | 'stats' | 'games' | 'settings';

export interface OpenPanelOptions {
  platform: Platform;
  auth?: AuthClientLike | null;
  /** Channel to tune to on first paint. Defaults to 'account' when signed-out
   *  and auth is on, else 'credits'. */
  section?: SectionId;
  /** Called once when the panel closes (Escape / backdrop / power glyph / a new
   *  open superseding it). Lets the opener (the chip) clear its pressed state. */
  onClose?: () => void;
}

export interface PanelHandle {
  /** Close the panel + restore focus to the opener. Idempotent. */
  close(): void;
  /** Whether the panel is currently mounted. */
  isOpen(): boolean;
}

const STYLE_ID = 'strafe-panel-styles';

// ---- the seven games + hub (friendly titles + slugs) -------------------------

interface GameLink {
  slug: string; // url segment (or '' for hub → '/')
  title: string;
}

const GAMES: readonly GameLink[] = [
  { slug: '', title: 'STRAFE.GAMES HUB' },
  { slug: 'strafe', title: 'STRAFE.GAMES' },
  { slug: 'zombie', title: 'DEADLIGHT' },
  { slug: 'gunner', title: 'ANGEL FLIGHT' },
  { slug: 'inkfall', title: 'INKFALL' },
  { slug: 'borrowedtime', title: 'BORROWED TIME' },
  { slug: 'echoloop', title: 'ECHOLOOP' },
  { slug: 'surf', title: 'COMBAT SURF' },
];

// Gameplay-bearing slugs only (hub excluded) — used for the per-game progress list.
const GAME_SLUGS: readonly string[] = GAMES.filter((g) => g.slug !== '').map((g) => g.slug);

const PLATFORM_VERSION = '1.0';

// ---- the five channels (the tuner rail) --------------------------------------

interface ChannelDef {
  id: SectionId;
  num: string; // '01'..'05'
  label: string; // rail key short label
  cap: string; // status-strip caption flavor
  word: string; // section heading plain word
  tuner: string; // one-line section sub-caption (flavor)
}

const CHANNELS: readonly ChannelDef[] = [
  { id: 'account', num: '01', label: 'ACCT', cap: '', word: 'ACCOUNT', tuner: 'Sign in to carry your credits across every game.' },
  { id: 'credits', num: '02', label: 'CRED', cap: '', word: 'CREDITS', tuner: 'Your shared balance across strafe.games.' },
  { id: 'stats', num: '03', label: 'STAT', cap: '', word: 'STATS', tuner: 'Your progress so far.' },
  { id: 'games', num: '04', label: 'GAME', cap: '', word: 'GAMES', tuner: 'Jump straight to any game.' },
  { id: 'settings', num: '05', label: 'SET', cap: '', word: 'SETTINGS', tuner: 'Account and platform options.' },
];

// ---- Cloudflare Turnstile (register-form bot protection) ---------------------
// Dormant-safe: NONE of this runs unless platform.turnstileSiteKey() is a
// non-empty string. The widget is rendered explicitly (not auto), so the script
// only ever acts on divs we create. Framework-agnostic, never throws.

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TURNSTILE_SCRIPT_ID = 'strafe-panel-turnstile';

interface TurnstileApi {
  render(el: HTMLElement, opts: { sitekey: string; callback?: (token: string) => void }): string;
  reset(widgetId?: string): void;
  getResponse(widgetId?: string): string | undefined;
}

function turnstileApi(): TurnstileApi | null {
  if (typeof window === 'undefined') return null;
  const t = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
  return t && typeof t.render === 'function' ? t : null;
}

function ensureTurnstileScript(): Promise<TurnstileApi | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);
  const ready = turnstileApi();
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve) => {
    const finish = (): void => resolve(turnstileApi());
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      let tries = 0;
      const iv = window.setInterval(() => {
        if (turnstileApi() || tries++ > 200) {
          window.clearInterval(iv);
          finish();
        }
      }, 50);
      return;
    }
    const s = document.createElement('script');
    s.id = TURNSTILE_SCRIPT_ID;
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', finish);
    s.addEventListener('error', () => resolve(null));
    document.head.appendChild(s);
  });
}

// ---- ref-counted single <style> ----------------------------------------------

let styleRefCount = 0;

function ensureStyle(): void {
  if (typeof document === 'undefined') return;
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function releaseStyle(): void {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount === 0) {
    const el = typeof document !== 'undefined' ? document.getElementById(STYLE_ID) : null;
    if (el) el.remove();
  }
}

// ---- styles (every selector `.strafe-panel-`-prefixed) -----------------------

const CSS = `
.strafe-panel-backdrop{position:fixed;inset:0;z-index:2147483600;pointer-events:auto;
  background:rgba(4,4,7,.62);-webkit-backdrop-filter:blur(5px);backdrop-filter:blur(5px);
  opacity:0;transition:opacity .22s ease;
  font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
  -webkit-font-smoothing:antialiased;}
.strafe-panel-backdrop[data-open="1"]{opacity:1;}

/* The rack docks to the right wall and slides in from the right; its left-edge
   handle pokes out so it reads as the closed chip handle having moved left. */
.strafe-panel-rack{pointer-events:auto;position:fixed;top:0;right:0;height:100vh;width:min(384px,92vw);
  box-sizing:border-box;display:flex;flex-direction:column;overflow:visible;color:#f2efe8;
  border-left:1px solid rgba(255,69,0,.30);
  background:linear-gradient(180deg,rgba(11,13,17,.985),rgba(7,8,10,.99));
  -webkit-backdrop-filter:blur(16px);backdrop-filter:blur(16px);
  box-shadow:-26px 0 70px rgba(0,0,0,.62),0 0 0 1px rgba(0,0,0,.4);
  transform:translateX(100%);
  transition:transform .34s cubic-bezier(.16,1,.3,1);}
.strafe-panel-backdrop[data-open="1"] .strafe-panel-rack{transform:translateX(0);}
.strafe-panel-rack:focus{outline:none;}

/* Left-edge close handle — points right (push the drawer back to the wall). */
.strafe-panel-handle{position:absolute;left:0;top:calc(env(safe-area-inset-top, 0px) + 64px);transform:translateX(-100%);
  display:inline-flex;align-items:center;justify-content:center;width:30px;height:44px;
  border:1px solid rgba(255,69,0,.30);border-right:0;border-radius:14px 0 0 14px;
  cursor:pointer;font-family:inherit;color:#ff7a3c;font-size:15px;font-weight:800;line-height:1;
  background:linear-gradient(180deg,rgba(11,13,17,.985),rgba(7,8,10,.99));
  box-shadow:-10px 0 26px rgba(0,0,0,.5);
  transition:color .16s ease,border-color .16s ease,width .18s cubic-bezier(.16,1,.3,1);}
.strafe-panel-handle:hover{width:35px;color:#FF4500;border-color:rgba(255,69,0,.7);}
.strafe-panel-handle:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}

/* Phone grab-bar — the bottom-sheet close affordance. */
.strafe-panel-grab{display:none;width:42px;height:4px;margin:9px auto 1px;padding:0;
  border:0;border-radius:3px;background:rgba(242,239,232,.26);cursor:pointer;}
.strafe-panel-grab:hover{background:rgba(255,69,0,.6);}
.strafe-panel-grab:focus-visible{outline:2px solid #ff7a3c;outline-offset:3px;}

.strafe-panel-head{flex:none;position:relative;display:flex;align-items:center;gap:12px;
  min-height:62px;padding:14px 18px;border-bottom:1px solid rgba(242,239,232,.10);}
.strafe-panel-brand{margin:0;font-weight:800;font-size:21px;line-height:1;letter-spacing:-.03em;
  text-transform:uppercase;color:#f2efe8;text-shadow:.05em .06em 0 rgba(255,69,0,.4);white-space:nowrap;}
.strafe-panel-brand .strafe-panel-dot{color:#FF4500;text-shadow:none;}
.strafe-panel-headspacer{flex:1 1 auto;}
.strafe-panel-bal{flex:none;display:flex;flex-direction:column;align-items:flex-end;gap:2px;}
.strafe-panel-balnum{font-size:22px;font-weight:800;color:#fff;line-height:1;font-variant-numeric:tabular-nums;}
.strafe-panel-balunit{font-size:9px;font-weight:700;letter-spacing:.16em;color:#ff7a3c;}

.strafe-panel-body{flex:1 1 auto;min-height:0;display:flex;}
.strafe-panel-rail{flex:none;width:60px;position:relative;display:flex;flex-direction:column;
  background:rgba(0,0,0,.30);border-right:1px solid rgba(242,239,232,.08);}
.strafe-panel-chan{flex:1 1 0;min-height:60px;border:0;background:transparent;cursor:pointer;font-family:inherit;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;color:#9aa3ad;
  border-bottom:1px solid rgba(242,239,232,.05);transition:background .14s ease,color .14s ease;}
.strafe-panel-chan:hover{background:rgba(255,69,0,.07);color:#f2efe8;}
.strafe-panel-chan:focus-visible{outline:2px solid #ff7a3c;outline-offset:-2px;}
.strafe-panel-channum{font-size:17px;font-weight:800;font-variant-numeric:tabular-nums;color:#9aa3ad;line-height:1;}
.strafe-panel-chanlabel{font-size:8px;font-weight:700;letter-spacing:.10em;}
.strafe-panel-chan[aria-selected="true"]{background:rgba(255,69,0,.12);color:#fff;}
.strafe-panel-chan[aria-selected="true"] .strafe-panel-channum{color:#fff;}
.strafe-panel-chan[aria-selected="true"] .strafe-panel-chanlabel{color:#ff7a3c;}
.strafe-panel-needle{position:absolute;right:-1px;width:3px;height:20%;background:#FF4500;top:0;
  transform:translateY(0);transition:transform .24s cubic-bezier(.16,1,.3,1);
  box-shadow:0 0 8px rgba(255,69,0,.7);border-radius:2px;pointer-events:none;}

.strafe-panel-viewport{flex:1 1 auto;min-width:0;position:relative;overflow-y:auto;
  padding:16px 18px 18px;-webkit-overflow-scrolling:touch;}
.strafe-panel-section{opacity:1;}

.strafe-panel-h{margin:0 0 5px;font-size:13px;font-weight:800;letter-spacing:.05em;color:#fff;text-transform:uppercase;}
.strafe-panel-sub{margin:0 0 15px;font-size:11.5px;line-height:1.5;color:#9aa3ad;}
.strafe-panel-divider{height:1px;background:rgba(242,239,232,.09);margin:17px 0;}

.strafe-panel-note{font-size:11px;line-height:1.5;letter-spacing:.01em;color:#c9cdd2;
  background:rgba(255,255,255,.03);border:1px solid rgba(242,239,232,.12);border-radius:10px;padding:11px 13px;}

.strafe-panel-card-tile{position:relative;background:rgba(255,255,255,.03);border:1px solid rgba(242,239,232,.10);
  border-radius:12px;padding:13px 14px;}
.strafe-panel-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;}
.strafe-panel-statlabel{font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:#9aa3ad;font-weight:700;}
.strafe-panel-statval{margin-top:5px;font-size:21px;font-weight:800;color:#fff;
  font-variant-numeric:tabular-nums;line-height:1;}

.strafe-panel-row{display:flex;align-items:center;gap:11px;padding:11px 13px;
  background:rgba(255,255,255,.03);border:1px solid rgba(242,239,232,.10);border-radius:11px;margin-bottom:8px;}
.strafe-panel-row:last-child{margin-bottom:0;}
.strafe-panel-rowmain{flex:1 1 auto;min-width:0;}
.strafe-panel-rowtitle{font-size:12px;font-weight:700;color:#f2efe8;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.strafe-panel-rowmeta{margin-top:3px;font-size:10px;letter-spacing:.04em;color:#9aa3ad;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}

.strafe-panel-price{flex:none;display:inline-flex;align-items:center;gap:5px;font-size:12px;
  font-weight:800;color:#fff;font-variant-numeric:tabular-nums;}
.strafe-panel-price .strafe-panel-coin{width:13px;height:13px;}
.strafe-panel-coin{width:14px;height:14px;border-radius:50%;flex:none;
  background:radial-gradient(circle at 35% 30%,#ffd9c2,#FF4500 66%,#b22f00);box-shadow:0 0 8px rgba(255,69,0,.45);}
.strafe-panel-buy{flex:none;min-height:34px;padding:0 14px;border:0;border-radius:9px;cursor:pointer;
  background:linear-gradient(135deg,#ff7a3c,#FF4500);color:#1a0a00;
  font-size:11px;font-weight:800;letter-spacing:.06em;font-family:inherit;
  transition:filter .12s ease,opacity .12s ease;}
.strafe-panel-buy:hover{filter:brightness(1.08);}
.strafe-panel-buy:disabled{opacity:.5;cursor:default;filter:none;}
.strafe-panel-buy:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}
.strafe-panel-pill{flex:none;min-height:30px;display:inline-flex;align-items:center;padding:0 11px;
  border-radius:8px;font-size:10px;font-weight:800;letter-spacing:.06em;}
.strafe-panel-pill[data-kind="owned"],.strafe-panel-pill[data-kind="free"]{
  background:rgba(255,69,0,.12);color:#ffb38a;border:1px solid rgba(255,69,0,.30);}

.strafe-panel-ledger{display:flex;align-items:center;justify-content:space-between;gap:10px;
  padding:8px 2px;border-bottom:1px solid rgba(242,239,232,.07);font-size:11px;}
.strafe-panel-ledger:last-child{border-bottom:0;}
.strafe-panel-ledgermain{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#c9cdd2;}
.strafe-panel-ledgerright{display:flex;align-items:center;gap:10px;flex:none;}
.strafe-panel-ledgerdate{flex:none;font-size:9px;letter-spacing:.06em;color:#565d66;}
.strafe-panel-delta{flex:none;font-weight:800;font-variant-numeric:tabular-nums;}
.strafe-panel-delta[data-sign="pos"]{color:#86e0a8;}
.strafe-panel-delta[data-sign="neg"]{color:#ff9c9c;}
.strafe-panel-empty{font-size:11px;color:#565d66;padding:8px 0;}

.strafe-panel-gamegrid{display:grid;grid-template-columns:1fr;gap:8px;}
.strafe-panel-gametile{display:flex;align-items:center;gap:12px;
  min-height:50px;padding:0 14px;border-radius:12px;cursor:pointer;text-align:left;
  border:1px solid rgba(242,239,232,.12);background:rgba(255,255,255,.03);
  color:#f2efe8;font-size:12.5px;font-weight:700;letter-spacing:.04em;font-family:inherit;
  transition:border-color .14s ease,background .14s ease,transform .14s ease;}
.strafe-panel-gametile:hover{border-color:rgba(255,69,0,.5);background:rgba(255,69,0,.07);transform:translateX(2px);}
.strafe-panel-gametile:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}
.strafe-panel-gametitle{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.strafe-panel-gochev{flex:none;color:#ff7a3c;font-size:16px;font-weight:800;line-height:1;
  transition:transform .14s ease;}
.strafe-panel-gametile:hover .strafe-panel-gochev{transform:translateX(3px);}

/* ACCOUNT form */
.strafe-panel-acctcard{display:flex;align-items:center;gap:13px;padding:14px;
  background:rgba(255,255,255,.03);border:1px solid rgba(242,239,232,.10);border-radius:12px;}
.strafe-panel-avatar{width:46px;height:46px;border-radius:50%;flex:none;object-fit:cover;
  background:radial-gradient(circle at 35% 30%,#ffd9c2,#FF4500 66%,#b22f00);}
.strafe-panel-mono{width:46px;height:46px;border-radius:50%;flex:none;display:grid;place-items:center;
  font-size:20px;font-weight:800;color:#1a0a00;background:radial-gradient(circle at 35% 30%,#ffd9c2,#FF4500 66%,#b22f00);}
.strafe-panel-acctinfo{flex:1 1 auto;min-width:0;}
.strafe-panel-acctname{font-size:14px;font-weight:800;color:#fff;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.strafe-panel-acctemail{margin-top:3px;font-size:11px;color:#9aa3ad;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.strafe-panel-segtabs{display:flex;gap:6px;padding:4px;border-radius:11px;margin-bottom:14px;
  background:rgba(0,0,0,.3);border:1px solid rgba(242,239,232,.08);}
.strafe-panel-segtab{flex:1;min-height:36px;border:0;border-radius:8px;cursor:pointer;
  background:transparent;color:#9aa3ad;font-size:11px;font-weight:700;
  letter-spacing:.06em;font-family:inherit;transition:background .12s ease,color .12s ease;}
.strafe-panel-segtab:hover{color:#f2efe8;}
.strafe-panel-segtab[aria-selected="true"]{background:rgba(255,69,0,.16);color:#fff;}
.strafe-panel-segtab:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}
.strafe-panel-field{display:flex;flex-direction:column;gap:5px;margin-bottom:12px;}
.strafe-panel-label{font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#9aa3ad;font-weight:700;}
.strafe-panel-input{min-height:44px;padding:0 12px;border-radius:10px;box-sizing:border-box;
  border:1px solid rgba(242,239,232,.16);background:rgba(0,0,0,.32);color:#fff;
  font-size:14px;font-family:inherit;transition:border-color .12s ease;}
.strafe-panel-input::placeholder{color:#565d66;}
.strafe-panel-input:focus{outline:none;border-color:rgba(255,69,0,.65);}
.strafe-panel-hint{font-size:10px;letter-spacing:.02em;color:#565d66;}
.strafe-panel-submit{width:100%;min-height:44px;border:0;border-radius:11px;cursor:pointer;
  background:linear-gradient(135deg,#ff7a3c,#FF4500);color:#1a0a00;
  font-size:13px;font-weight:800;letter-spacing:.06em;font-family:inherit;transition:filter .12s ease,opacity .12s ease;}
.strafe-panel-submit:hover{filter:brightness(1.08);}
.strafe-panel-submit:disabled{opacity:.6;cursor:default;filter:none;}
.strafe-panel-submit:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}
.strafe-panel-signout{width:100%;min-height:44px;margin-top:13px;border-radius:11px;cursor:pointer;
  background:transparent;border:1px solid rgba(255,156,156,.36);color:#ff9c9c;
  font-size:12px;font-weight:700;letter-spacing:.06em;font-family:inherit;transition:background .12s ease,border-color .12s ease;}
.strafe-panel-signout:hover{background:rgba(255,156,156,.1);border-color:rgba(255,156,156,.55);}
.strafe-panel-signout:focus-visible{outline:2px solid #ff7a3c;outline-offset:2px;}
.strafe-panel-error{min-height:15px;margin-bottom:10px;font-size:11px;line-height:1.4;color:#ff9c9c;}
.strafe-panel-error:empty{display:none;}
.strafe-panel-turnstile{display:flex;justify-content:center;margin-bottom:12px;}
.strafe-panel-turnstile:empty{display:none;}
.strafe-panel-about{font-size:10px;letter-spacing:.03em;color:#9aa3ad;line-height:1.6;}

.strafe-panel-foot{flex:none;border-top:1px solid rgba(242,239,232,.10);padding:10px 18px;}
.strafe-panel-footnote{font-size:10px;letter-spacing:.02em;color:#9aa3ad;line-height:1.5;}

.strafe-panel-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;
  overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}

/* Phone: full-width bottom sheet (no left handle; a top grab-bar closes it). */
@media (max-width:639px){
  .strafe-panel-backdrop{align-items:flex-end;}
  .strafe-panel-rack{top:auto;right:0;left:0;bottom:0;width:100%;height:auto;max-height:92vh;overflow:hidden;
    border-left:0;border-top:1px solid rgba(255,69,0,.30);border-radius:16px 16px 0 0;
    transform:translateY(100%);box-shadow:0 -22px 60px rgba(0,0,0,.6);
    padding-bottom:env(safe-area-inset-bottom,0px);}
  .strafe-panel-backdrop[data-open="1"] .strafe-panel-rack{transform:translateY(0);}
  .strafe-panel-handle{display:none;}
  .strafe-panel-grab{display:block;}
  .strafe-panel-head{padding-top:max(8px,env(safe-area-inset-top,0px));}
  .strafe-panel-body{flex-direction:column;}
  .strafe-panel-rail{width:100%;flex-direction:row;height:54px;flex:none;overflow-x:auto;
    border-right:0;border-bottom:1px solid rgba(242,239,232,.08);scroll-snap-type:x mandatory;}
  .strafe-panel-rail::-webkit-scrollbar{display:none;}
  .strafe-panel-chan{flex:none;min-width:64px;height:54px;scroll-snap-align:start;
    border-bottom:0;border-right:1px solid rgba(242,239,232,.05);}
  .strafe-panel-needle{right:auto;left:0;bottom:0;top:auto;width:64px;height:3px;transform:translateX(0);}
  .strafe-panel-grid{grid-template-columns:1fr 1fr;}
}

@media (prefers-reduced-motion:reduce){
  .strafe-panel-backdrop,.strafe-panel-rack{transition:opacity .12s ease;}
  .strafe-panel-rack{transform:translateX(0) !important;}
  .strafe-panel-needle,.strafe-panel-handle{transition:none;}
  .strafe-panel-chan:hover,.strafe-panel-gametile:hover{transform:none;}
}
@media (max-width:639px) and (prefers-reduced-motion:reduce){
  .strafe-panel-rack{transform:translateY(0) !important;}
}
`;

// ---- helpers -----------------------------------------------------------------

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

async function safeAsync(fn: () => Promise<unknown> | unknown): Promise<void> {
  try {
    await fn();
  } catch {
    /* never throws */
  }
}

function uid(): string {
  return Math.random().toString(36).slice(2, 8);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
}

function coin(): HTMLElement {
  const c = el('span', 'strafe-panel-coin');
  c.setAttribute('aria-hidden', 'true');
  return c;
}

function isPhone(): boolean {
  return safe(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width:639px)').matches,
    false,
  );
}

/** Relative-time label for a ledger timestamp; never throws. */
function relTime(t: number): string {
  const now = Date.now();
  const diff = Math.max(0, now - t);
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return safe(() => new Date(t).toISOString().slice(0, 10), '');
}

/** Friendly label for a raw ledger reason code. */
function reasonLabel(r: string): string {
  switch (r) {
    case 'welcome_bonus':
      return 'Welcome bonus';
    case 'first_clear':
    case 'clear':
      return 'Level cleared';
    case 'playtime':
      return 'Playtime reward';
    case 'earn':
      return 'Earned credits';
    case 'unlock':
      return 'Chapter unlocked';
    case 'ad':
      return 'Watched an ad';
    case 'migrated_to_account':
      return 'Moved to account';
    default:
      return r ? r.replace(/_/g, ' ') : 'Credits';
  }
}

/** Friendly, leak-free message for a server error code (mirrors the chip). */
function friendlyAuthError(code: string): string {
  switch (code) {
    case 'invalid_credentials':
      return 'Wrong email or password';
    case 'email_taken':
      return 'That email is already registered';
    case 'invalid_email':
      return 'Enter a valid email address';
    case 'weak_password':
      return 'Password must be at least 8 characters';
    case 'rate_limited':
      return 'Too many attempts, try again later';
    case 'auth_disabled':
      return 'Sign-in is unavailable right now';
    default:
      return 'Something went wrong';
  }
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

// ---- single-instance guard ---------------------------------------------------

let active: PanelHandle | null = null;

const NOOP_HANDLE: PanelHandle = {
  close() {},
  isOpen() {
    return false;
  },
};

// ---- open --------------------------------------------------------------------

export function openPlatformPanel(opts: OpenPanelOptions): PanelHandle {
  if (typeof document === 'undefined') return NOOP_HANDLE;
  // One panel at a time — re-opening closes the prior instance.
  if (active) active.close();

  const { platform } = opts;
  const auth = opts.auth ?? null;
  const authActive = auth !== null && safe(() => auth.isAuthEnabled(), false);

  ensureStyle();

  const lastFocus = (document.activeElement as HTMLElement) ?? null;
  let session: AuthSessionLike | null = null;
  let closed = false;

  // ---- backdrop + rack ----
  const backdrop = el('div', 'strafe-panel-backdrop');
  const rack = el('div', 'strafe-panel-rack');
  rack.setAttribute('role', 'dialog');
  rack.setAttribute('aria-modal', 'true');
  rack.tabIndex = -1;
  const titleId = `strafe-panel-title-${uid()}`;
  rack.setAttribute('aria-labelledby', titleId);

  // ---- head: hub-style wordmark (left) · live balance (right) ----
  const head = el('div', 'strafe-panel-head');
  const brand = el('h2', 'strafe-panel-brand');
  brand.id = titleId;
  brand.append(document.createTextNode('strafe'));
  brand.append(el('span', 'strafe-panel-dot', '.games'));

  const headSpacer = el('div', 'strafe-panel-headspacer');

  const balBox = el('div', 'strafe-panel-bal');
  const balNum = el('span', 'strafe-panel-balnum', '0');
  balNum.setAttribute('aria-hidden', 'true');
  const balUnit = el('span', 'strafe-panel-balunit', 'CREDITS');
  balUnit.setAttribute('aria-hidden', 'true');
  balBox.append(balNum, balUnit);

  head.append(brand, headSpacer, balBox);

  // ---- close affordances: right-wall handle (desktop) + grab-bar (phone) ----
  const closeHandle = el('button', 'strafe-panel-handle', '›');
  closeHandle.type = 'button';
  closeHandle.setAttribute('aria-label', 'Close menu');
  closeHandle.addEventListener('click', () => handle.close());

  const grab = el('button', 'strafe-panel-grab');
  grab.type = 'button';
  grab.setAttribute('aria-label', 'Close menu');
  grab.addEventListener('click', () => handle.close());

  // ---- body: tuner rail + viewport ----
  const body = el('div', 'strafe-panel-body');
  const rail = el('div', 'strafe-panel-rail');
  rail.setAttribute('role', 'tablist');
  const viewport = el('div', 'strafe-panel-viewport');

  const chanButtons = new Map<SectionId, HTMLButtonElement>();
  const sections = new Map<SectionId, HTMLElement>();
  const renderers = new Map<SectionId, () => void>();

  for (const def of CHANNELS) {
    const key = el('button', 'strafe-panel-chan');
    key.type = 'button';
    key.setAttribute('role', 'tab');
    key.id = `strafe-panel-chan-${def.id}-${uid()}`;
    key.tabIndex = -1;
    key.append(
      el('span', 'strafe-panel-channum', def.num),
      el('span', 'strafe-panel-chanlabel', def.label),
    );
    key.addEventListener('click', () => selectChannel(def.id));
    chanButtons.set(def.id, key);
    rail.append(key);

    const sec = el('div', 'strafe-panel-section');
    sec.setAttribute('role', 'tabpanel');
    sec.setAttribute('aria-labelledby', key.id);
    sec.style.display = 'none';
    key.setAttribute('aria-controls', `strafe-panel-sec-${def.id}-${uid()}`);
    sec.id = key.getAttribute('aria-controls')!;
    sections.set(def.id, sec);
    viewport.append(sec);
  }

  const needle = el('i', 'strafe-panel-needle');
  needle.setAttribute('aria-hidden', 'true');
  rail.append(needle);

  body.append(rail, viewport);

  // ---- foot (honesty note) ----
  const foot = el('div', 'strafe-panel-foot');
  const footNote = el('div', 'strafe-panel-footnote');
  foot.append(footNote);

  function renderFoot(): void {
    if (authActive && session === null) {
      footNote.textContent = 'Credits are saved on this device — sign in to carry them everywhere.';
      foot.style.display = '';
    } else {
      foot.style.display = 'none';
    }
  }

  let currentTab: SectionId =
    opts.section ?? (authActive && session === null ? 'account' : 'credits');

  // ---- needle positioning (read offsets once per tune; never per-frame) ----
  function positionNeedle(): void {
    const key = chanButtons.get(currentTab);
    if (!key) return;
    if (isPhone()) {
      rail.setAttribute('aria-orientation', 'horizontal');
      needle.style.transform = `translateX(${key.offsetLeft}px)`;
    } else {
      rail.setAttribute('aria-orientation', 'vertical');
      needle.style.transform = `translateY(${key.offsetTop}px)`;
    }
  }

  function selectChannel(id: SectionId): void {
    currentTab = id;
    for (const [cid, btn] of chanButtons) {
      const on = cid === id;
      btn.setAttribute('aria-selected', String(on));
      btn.tabIndex = on ? 0 : -1;
    }
    for (const [sid, sec] of sections) {
      const on = sid === id;
      sec.style.display = on ? '' : 'none';
      sec.setAttribute('data-active', on ? '1' : '0');
    }
    const r = renderers.get(id);
    if (r) safe(r, undefined);
    // The content swaps instantly; only the tuner needle glides to the channel.
    positionNeedle();
  }

  // Arrow / Home / End nav on the rail (roving tabindex).
  rail.addEventListener('keydown', (e) => {
    const ids = CHANNELS.map((d) => d.id);
    const cur = ids.indexOf(currentTab);
    let next = -1;
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = cur - 1;
    else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = cur + 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ids.length - 1;
    else return;
    e.preventDefault();
    if (next < 0) next = ids.length - 1;
    if (next >= ids.length) next = 0;
    const nid = ids[next]!;
    selectChannel(nid);
    const btn = chanButtons.get(nid);
    if (btn) safe(() => btn.focus(), undefined);
  });

  function monogram(name: string): HTMLElement {
    const m = el('span', 'strafe-panel-mono');
    m.textContent = (name.trim()[0] ?? '?').toUpperCase();
    return m;
  }

  /** Section heading: plain word + a short sub-caption. */
  function chHead(sec: HTMLElement, def: ChannelDef): void {
    sec.append(el('h3', 'strafe-panel-h', def.word));
    sec.append(el('p', 'strafe-panel-sub', def.tuner));
  }

  const DEF = (id: SectionId): ChannelDef => CHANNELS.find((d) => d.id === id)!;

  // =====================================================================
  // CH-01 — ACCOUNT
  // =====================================================================
  {
    const sec = sections.get('account')!;
    let mode: 'login' | 'register' = 'login';

    function render(): void {
      sec.replaceChildren();
      chHead(sec, DEF('account'));

      // Auth DISABLED → subtle "coming soon", no network, no form.
      if (!authActive || !auth) {
        sec.append(
          el(
            'div',
            'strafe-panel-note',
            'Accounts coming soon. Your credits are saved on this device and carry across every strafe.games game.',
          ),
        );
        return;
      }

      // Signed in → station-ident card + sign out.
      if (session) {
        const acct = el('div', 'strafe-panel-acctcard');
        if (session.avatarUrl) {
          const img = el('img', 'strafe-panel-avatar');
          img.referrerPolicy = 'no-referrer';
          img.alt = '';
          img.src = session.avatarUrl;
          img.addEventListener('error', () => img.replaceWith(monogram(session!.displayName)));
          acct.append(img);
        } else {
          acct.append(monogram(session.displayName));
        }
        const info = el('div', 'strafe-panel-acctinfo');
        info.append(el('div', 'strafe-panel-acctname', session.displayName || 'Player'));
        if (session.email) info.append(el('div', 'strafe-panel-acctemail', session.email));
        acct.append(info);
        sec.append(acct);

        const out = el('button', 'strafe-panel-signout', 'Sign out');
        out.type = 'button';
        out.addEventListener('click', () => void safeAsync(() => auth.signOut()));
        sec.append(out);
        return;
      }

      // Signed out → email/password form with Login/Register toggle.
      const seg = el('div', 'strafe-panel-segtabs');
      seg.setAttribute('role', 'tablist');
      const loginTab = el('button', 'strafe-panel-segtab', 'Login');
      loginTab.type = 'button';
      loginTab.setAttribute('role', 'tab');
      const regTab = el('button', 'strafe-panel-segtab', 'Register');
      regTab.type = 'button';
      regTab.setAttribute('role', 'tab');
      seg.append(loginTab, regTab);

      const form = el('form');
      form.noValidate = true;

      const emailField = el('div', 'strafe-panel-field');
      const emailId = `strafe-panel-email-${uid()}`;
      const emailLabel = el('label', 'strafe-panel-label', 'Email');
      emailLabel.htmlFor = emailId;
      const emailInput = el('input', 'strafe-panel-input');
      emailInput.id = emailId;
      emailInput.type = 'email';
      emailInput.autocomplete = 'email';
      emailInput.placeholder = 'you@example.com';
      emailField.append(emailLabel, emailInput);

      const pwField = el('div', 'strafe-panel-field');
      const pwId = `strafe-panel-pw-${uid()}`;
      const pwLabel = el('label', 'strafe-panel-label', 'Password');
      pwLabel.htmlFor = pwId;
      const pwInput = el('input', 'strafe-panel-input');
      pwInput.id = pwId;
      pwInput.type = 'password';
      pwInput.placeholder = '••••••••';
      const pwHint = el('div', 'strafe-panel-hint', 'At least 8 characters');
      pwHint.style.display = 'none';
      pwField.append(pwLabel, pwInput, pwHint);

      const turnstileHost = el('div', 'strafe-panel-turnstile');
      const siteKey = safe(() => platform.turnstileSiteKey(), '');
      let widgetId: string | null = null;
      let turnstileLoading = false;

      function ensureTurnstileWidget(): void {
        if (siteKey.length === 0 || widgetId !== null || turnstileLoading) return;
        turnstileLoading = true;
        void safeAsync(async () => {
          const api = await ensureTurnstileScript();
          if (!api || !document.contains(turnstileHost)) {
            turnstileLoading = false;
            return;
          }
          const id = safe(() => api.render(turnstileHost, { sitekey: siteKey }) as string | null, null);
          widgetId = typeof id === 'string' ? id : null;
          turnstileLoading = false;
        });
      }
      function readTurnstileToken(): string {
        if (siteKey.length === 0) return '';
        const api = turnstileApi();
        const viaApi = api ? safe(() => api.getResponse(widgetId ?? undefined) ?? '', '') : '';
        if (viaApi.length > 0) return viaApi;
        const hidden = turnstileHost.querySelector<HTMLInputElement>(
          'input[name="cf-turnstile-response"]',
        );
        return hidden && typeof hidden.value === 'string' ? hidden.value : '';
      }
      function resetTurnstile(): void {
        if (siteKey.length === 0 || widgetId === null) return;
        const api = turnstileApi();
        if (api) safe(() => api.reset(widgetId ?? undefined), undefined);
      }

      const errorLine = el('div', 'strafe-panel-error');
      errorLine.setAttribute('role', 'alert');
      errorLine.setAttribute('aria-live', 'assertive');

      const submit = el('button', 'strafe-panel-submit');
      submit.type = 'submit';

      form.append(emailField, pwField, turnstileHost, errorLine, submit);

      let busy = false;
      function syncMode(): void {
        const isLogin = mode === 'login';
        submit.textContent = isLogin ? 'Sign in' : 'Create account';
        pwInput.autocomplete = isLogin ? 'current-password' : 'new-password';
        pwHint.style.display = isLogin ? 'none' : '';
        loginTab.setAttribute('aria-selected', String(isLogin));
        regTab.setAttribute('aria-selected', String(!isLogin));
        errorLine.textContent = '';
        turnstileHost.style.display = isLogin ? 'none' : '';
        if (!isLogin) ensureTurnstileWidget();
      }
      loginTab.addEventListener('click', () => {
        if (busy || mode === 'login') return;
        mode = 'login';
        syncMode();
      });
      regTab.addEventListener('click', () => {
        if (busy || mode === 'register') return;
        mode = 'register';
        syncMode();
      });

      async function doSubmit(): Promise<void> {
        if (busy) return;
        const email = emailInput.value.trim();
        const password = pwInput.value;
        if (email.length === 0 || !email.includes('@')) {
          errorLine.textContent = friendlyAuthError('invalid_email');
          safe(() => emailInput.focus(), undefined);
          return;
        }
        if (mode === 'register' && password.length < 8) {
          errorLine.textContent = friendlyAuthError('weak_password');
          safe(() => pwInput.focus(), undefined);
          return;
        }
        if (password.length === 0) {
          errorLine.textContent = friendlyAuthError('invalid_credentials');
          safe(() => pwInput.focus(), undefined);
          return;
        }
        const fn = mode === 'login' ? auth!.login : auth!.register;
        if (typeof fn !== 'function') {
          errorLine.textContent = friendlyAuthError('auth_disabled');
          return;
        }
        busy = true;
        submit.disabled = true;
        errorLine.textContent = '';
        const prevLabel = submit.textContent;
        submit.textContent = mode === 'login' ? 'Signing in…' : 'Creating…';

        let result: AuthResultLike;
        try {
          result =
            mode === 'register'
              ? await auth!.register!(email, password, readTurnstileToken())
              : await auth!.login!(email, password);
        } catch {
          result = { ok: false, error: 'failed' };
        }
        if (closed || !document.contains(form)) return;
        if (result.ok) {
          // The auth subscribe path re-renders us to signed-in (or reloads).
          return;
        }
        busy = false;
        submit.disabled = false;
        submit.textContent = prevLabel;
        errorLine.textContent = friendlyAuthError(result.error);
        if (mode === 'register') resetTurnstile();
      }

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        void safeAsync(doSubmit);
      });

      sec.append(seg, form);
      syncMode();
    }

    renderers.set('account', render);
  }

  // =====================================================================
  // CH-02 — CREDITS (balance + earn + STORE + HISTORY)
  // =====================================================================
  {
    const sec = sections.get('credits')!;

    function render(): void {
      sec.replaceChildren();
      const free = safe(() => platform.flags.unlockAll === true, false);
      const bal = safe(() => platform.balance(), 0);

      chHead(sec, DEF('credits'));

      // Big balance meter tile
      const balCard = el('div', 'strafe-panel-card-tile');
      const big = el('div', 'strafe-panel-balrow');
      const n = el('span', 'strafe-panel-balnum', String(bal));
      n.style.fontSize = '32px';
      big.append(n, el('span', 'strafe-panel-balunit', 'CREDITS'));
      balCard.append(big);
      const explain = el('p', 'strafe-panel-sub', 'Earn credits as you play — level clears, playtime, and more.');
      explain.style.margin = '8px 0 0';
      balCard.append(explain);
      sec.append(balCard);

      // STORE
      sec.append(el('div', 'strafe-panel-divider'));
      sec.append(el('h3', 'strafe-panel-h', 'STORE'));
      const items = safe(() => platform.storeItems(), []);
      if (items.length === 0) {
        sec.append(el('div', 'strafe-panel-empty', 'Nothing to unlock yet.'));
      } else {
        for (const it of items) sec.append(storeRow(it, free, bal, render));
      }

      // HISTORY
      sec.append(el('div', 'strafe-panel-divider'));
      sec.append(el('h3', 'strafe-panel-h', 'HISTORY'));
      const ledger = safe(() => platform.getLedger(), []);
      if (ledger.length === 0) {
        sec.append(el('div', 'strafe-panel-empty', 'No activity yet.'));
      } else {
        const recent = ledger.slice(-12).reverse();
        for (const e of recent) {
          const rowEl = el('div', 'strafe-panel-ledger');
          const main = el('div', 'strafe-panel-ledgermain', reasonLabel(e.r));
          const right = el('div', 'strafe-panel-ledgerright');
          const date = el('span', 'strafe-panel-ledgerdate', relTime(e.t));
          const delta = el('span', 'strafe-panel-delta', `${e.d >= 0 ? '+' : ''}${e.d}`);
          delta.setAttribute('data-sign', e.d >= 0 ? 'pos' : 'neg');
          right.append(date, delta);
          rowEl.append(main, right);
          sec.append(rowEl);
        }
      }
    }

    function storeRow(
      it: { gameId: string; sku: string; price: number; title: string; owned: boolean },
      free: boolean,
      bal: number,
      rerender: () => void,
    ): HTMLElement {
      const row = el('div', 'strafe-panel-row');
      const main = el('div', 'strafe-panel-rowmain');
      main.append(el('div', 'strafe-panel-rowtitle', it.title));
      const gameTitle = GAMES.find((g) => g.slug === it.gameId)?.title ?? it.gameId.toUpperCase();
      main.append(el('div', 'strafe-panel-rowmeta', gameTitle));
      row.append(main);

      if (it.owned) {
        const pill = el('span', 'strafe-panel-pill', 'OWNED');
        pill.setAttribute('data-kind', 'owned');
        row.append(pill);
        return row;
      }
      if (free) {
        const pill = el('span', 'strafe-panel-pill', 'INCLUDED');
        pill.setAttribute('data-kind', 'free');
        row.append(pill);
        return row;
      }

      const price = el('span', 'strafe-panel-price');
      price.append(coin(), document.createTextNode(String(it.price)));
      row.append(price);

      const buy = el('button', 'strafe-panel-buy', 'Buy');
      buy.type = 'button';
      if (bal < it.price) buy.disabled = true;
      buy.addEventListener('click', () => {
        const res = safe(
          () => platform.unlock(`${it.gameId}:${it.sku}`, it.price),
          'insufficient' as const,
        );
        if (res === 'insufficient') {
          buy.textContent = 'Not enough';
          buy.disabled = true;
          window.setTimeout(() => {
            buy.textContent = 'Buy';
            buy.disabled = safe(() => platform.balance(), 0) < it.price;
          }, 1400);
          return;
        }
        rerender();
      });
      row.append(buy);
      return row;
    }

    renderers.set('credits', render);
  }

  // =====================================================================
  // CH-03 — STATS
  // =====================================================================
  {
    const sec = sections.get('stats')!;

    function render(): void {
      sec.replaceChildren();
      const bal = safe(() => platform.balance(), 0);
      const ledger = safe(() => platform.getLedger(), []);
      const totalEarned = ledger.reduce((sum, e) => (e.d > 0 ? sum + e.d : sum), 0);
      const owned = safe(() => platform.storeItems(), []).filter((i) => i.owned).length;

      chHead(sec, DEF('stats'));

      const grid = el('div', 'strafe-panel-grid');
      grid.append(statTile('Balance', String(bal)));
      grid.append(statTile('Total earned', String(totalEarned)));
      grid.append(statTile('Chapters owned', String(owned)));
      grid.append(statTile('Games', String(GAME_SLUGS.length)));
      sec.append(grid);

      sec.append(el('div', 'strafe-panel-divider'));
      sec.append(el('h3', 'strafe-panel-h', 'PROGRESS'));
      let any = false;
      for (const slug of GAME_SLUGS) {
        const prog = safe(() => platform.getProgress<unknown>(slug, null), null);
        if (prog === null || prog === undefined) continue;
        any = true;
        const title = GAMES.find((g) => g.slug === slug)?.title ?? slug.toUpperCase();
        const row = el('div', 'strafe-panel-row');
        const main = el('div', 'strafe-panel-rowmain');
        main.append(el('div', 'strafe-panel-rowtitle', title));
        main.append(el('div', 'strafe-panel-rowmeta', summarizeProgress(prog)));
        row.append(main);
        sec.append(row);
      }
      if (!any) {
        sec.append(el('div', 'strafe-panel-empty', 'No saved progress yet — go play something!'));
      }
    }

    function statTile(label: string, value: string): HTMLElement {
      const t = el('div', 'strafe-panel-card-tile');
      t.append(el('div', 'strafe-panel-statlabel', label));
      t.append(el('div', 'strafe-panel-statval', value));
      return t;
    }

    function summarizeProgress(p: unknown): string {
      if (typeof p === 'number') return `level ${p}`;
      if (p && typeof p === 'object') {
        const o = p as Record<string, unknown>;
        const parts: string[] = [];
        for (const k of ['level', 'best', 'wave', 'score', 'cleared', 'unlocked']) {
          const v = o[k];
          if (typeof v === 'number' || typeof v === 'string') parts.push(`${k}: ${v}`);
        }
        if (parts.length > 0) return parts.slice(0, 3).join(' · ');
        const keys = Object.keys(o);
        return keys.length > 0 ? `${keys.length} saved value${keys.length === 1 ? '' : 's'}` : 'saved';
      }
      return 'saved';
    }

    renderers.set('stats', render);
  }

  // =====================================================================
  // CH-04 — GAMES (channel guide)
  // =====================================================================
  {
    const sec = sections.get('games')!;

    function render(): void {
      sec.replaceChildren();
      chHead(sec, DEF('games'));
      const grid = el('div', 'strafe-panel-gamegrid');
      GAMES.forEach((g) => {
        const tile = el('button', 'strafe-panel-gametile');
        tile.type = 'button';
        tile.append(el('span', 'strafe-panel-gametitle', g.title));
        const chev = el('span', 'strafe-panel-gochev', '›');
        chev.setAttribute('aria-hidden', 'true');
        tile.append(chev);
        const href = g.slug === '' ? '/' : `/${g.slug}/`;
        tile.setAttribute('aria-label', `Go to ${g.title}`);
        tile.addEventListener('click', () => {
          safe(() => window.location.assign(href), undefined);
        });
        grid.append(tile);
      });
      sec.append(grid);
    }

    renderers.set('games', render);
  }

  // =====================================================================
  // CH-05 — SETTINGS
  // =====================================================================
  {
    const sec = sections.get('settings')!;

    function render(): void {
      sec.replaceChildren();
      chHead(sec, DEF('settings'));
      sec.append(
        el(
          'p',
          'strafe-panel-sub',
          'Most gameplay options live inside each game. This is your shared account surface.',
        ),
      );

      if (authActive && auth && session) {
        const out = el('button', 'strafe-panel-signout', 'Sign out');
        out.type = 'button';
        out.style.marginTop = '0';
        out.addEventListener('click', () => void safeAsync(() => auth.signOut()));
        sec.append(out);
        sec.append(el('div', 'strafe-panel-divider'));
      }

      sec.append(el('h3', 'strafe-panel-h', 'ABOUT'));
      const about = el('div', 'strafe-panel-about');
      about.append(document.createTextNode(`strafe.games platform v${PLATFORM_VERSION}`));
      about.append(el('br'));
      about.append(document.createTextNode('One wallet shared across every game on strafe.games.'));
      sec.append(about);
    }

    renderers.set('settings', render);
  }

  // ---- balance readout (header) ----
  function renderHeaderBalance(): void {
    const bal = safe(() => platform.balance(), 0);
    balNum.textContent = String(bal);
    balUnit.textContent = 'CREDITS';
  }

  // ---- assemble ----
  rack.append(grab, head, body, foot, closeHandle);
  backdrop.append(rack);

  // backdrop click (outside the rack) closes
  backdrop.addEventListener('pointerdown', (e) => {
    if (e.target === backdrop) handle.close();
  });

  // ---- focus trap + Escape ----
  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handle.close();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = Array.from(rack.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (n) => n.offsetParent !== null || n === document.activeElement,
    );
    if (focusables.length === 0) {
      e.preventDefault();
      rack.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const activeEl = document.activeElement;
    if (e.shiftKey && activeEl === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && activeEl === last) {
      e.preventDefault();
      first.focus();
    }
  }
  document.addEventListener('keydown', onKeyDown, true);

  // ---- recompute needle on resize (orientation may flip across 640px) ----
  let resizeTimer = 0;
  const onResize = (): void => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => positionNeedle(), 120);
  };
  window.addEventListener('resize', onResize);

  // ---- live platform balance subscription ----
  const offPlatform = safe(
    () =>
      platform.onChange(() => {
        renderHeaderBalance();
        renderFoot();
        if (currentTab === 'credits' || currentTab === 'stats') {
          const r = renderers.get(currentTab);
          if (r) safe(r, undefined);
        }
      }),
    () => {},
  );

  // ---- auth subscription + initial session pull ----
  let offAuth: () => void = () => {};
  if (authActive && auth) {
    offAuth = safe(
      () =>
        auth.subscribe((s) => {
          session = s;
          renderHeaderBalance();
          renderFoot();
          const r = renderers.get('account');
          if (r) safe(r, undefined);
          if (currentTab === 'settings') {
            const rs = renderers.get('settings');
            if (rs) safe(rs, undefined);
          }
        }),
      () => {},
    );
    void safeAsync(async () => {
      const s = await auth.getSession();
      session = s;
      renderFoot();
      const r = renderers.get('account');
      if (r) safe(r, undefined);
    });
  }

  // ---- mount ----
  document.body.append(backdrop);
  renderHeaderBalance();
  renderFoot();
  selectChannel(currentTab);

  // Trigger the open transition + lock the needle offsets next frame.
  requestAnimationFrame(() => {
    backdrop.setAttribute('data-open', '1');
    positionNeedle();
  });

  // Initial focus: the signed-out ACCOUNT form's first input, else the active
  // channel key, else the rack.
  requestAnimationFrame(() => {
    let target: HTMLElement | null = null;
    if (currentTab === 'account' && authActive && session === null) {
      target = rack.querySelector<HTMLElement>('.strafe-panel-input');
    }
    if (!target) target = chanButtons.get(currentTab) ?? null;
    safe(() => (target ?? rack).focus(), undefined);
  });

  const handle: PanelHandle = {
    close(): void {
      if (closed) return;
      closed = true;
      offPlatform();
      offAuth();
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onResize);
      window.clearTimeout(resizeTimer);
      backdrop.removeAttribute('data-open');
      const finish = (): void => {
        backdrop.remove();
        releaseStyle();
      };
      let done = false;
      const settle = (): void => {
        if (done) return;
        done = true;
        finish();
      };
      backdrop.addEventListener('transitionend', settle, { once: true });
      window.setTimeout(settle, 340);
      if (lastFocus && document.contains(lastFocus)) safe(() => lastFocus.focus(), undefined);
      if (active === handle) active = null;
      if (typeof opts.onClose === 'function') safe(() => opts.onClose!(), undefined);
    },
    isOpen(): boolean {
      return !closed;
    },
  };
  active = handle;
  return handle;
}

// ---- debug auto-open hook ----------------------------------------------------

/** If the URL contains a `panel` query flag, auto-open the panel once on boot.
 *  Tiny + harmless: it only acts when `?panel`/`?panel=1` is present. A value
 *  matching a channel id (e.g. `?panel=games`) opens straight to that channel,
 *  which makes each channel screenshot-verifiable without a click. Call this
 *  from a game's boot after the chip is mounted. */
export function maybeAutoOpenPanel(opts: OpenPanelOptions): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const search = safe(() => window.location.search, '');
  const m = /[?&]panel(?:=([^&]*))?/.exec(search);
  if (!m) return;
  const raw = (m[1] ?? '').toLowerCase();
  const valid: SectionId[] = ['account', 'credits', 'stats', 'games', 'settings'];
  const section = (valid as string[]).includes(raw) ? (raw as SectionId) : opts.section;
  const open = (): void => {
    safe(() => openPlatformPanel({ ...opts, section }), NOOP_HANDLE);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', open, { once: true });
  } else {
    open();
  }
}
